import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button } from '../components/common';
import { negativePathwayContent } from '../config/negativePathwayContent';
import './NegativePathway.scss';

/**
 * Renders additional content sections based on type
 */
const AdditionalContentRenderer = ({ item }) => {
  if (item.type === 'section') {
    return (
      <p>
        {item.title && <strong>{item.title} </strong>}
        {item.content && `${item.content} `}
        {item.linkText && (
          <a href={item.linkHref} target="_blank" rel="noopener noreferrer">
            {item.linkText}
          </a>
        )}
        {item.afterLinkText && ` ${item.afterLinkText} `}
        {item.secondLinkText && (
          <a href={item.secondLinkHref} target="_blank" rel="noopener noreferrer">
            {item.secondLinkText}
          </a>
        )}
        {item.endText && ` ${item.endText}`}
      </p>
    );
  }

  if (item.type === 'paragraph') {
    return (
      <p>
        {item.content && `${item.content} `}
        {item.linkText && (
          <a href={item.linkHref} target="_blank" rel="noopener noreferrer">
            {item.linkText}
          </a>
        )}
        {item.afterLinkText && ` ${item.afterLinkText} `}
        {item.secondLinkText && (
          <a href={item.secondLinkHref} target="_blank" rel="noopener noreferrer">
            {item.secondLinkText}
          </a>
        )}
        {item.endText && ` ${item.endText}`}
      </p>
    );
  }

  return null;
};

AdditionalContentRenderer.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
    content: PropTypes.string,
    linkText: PropTypes.string,
    linkHref: PropTypes.string,
    afterLinkText: PropTypes.string,
    secondLinkText: PropTypes.string,
    secondLinkHref: PropTypes.string,
    endText: PropTypes.string
  }).isRequired
};

/**
 * NegativePathway page component
 * Displays when a user cannot proceed with the complaint checker
 */
export const NegativePathway = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { contentKey } = location.state || {};
  const content = contentKey ? negativePathwayContent[contentKey] : null;

  const handleBack = () => {
    const questionNumber = content?.questionNumber || 1;
    navigate('/complaint-checker', { state: { returnToQuestion: questionNumber } });
  };

  const handleReturnHome = () => {
    // Clear all complaint checker progress from storage
    sessionStorage.removeItem('complaint_checker_question');
    sessionStorage.removeItem('answer_q1');
    sessionStorage.removeItem('answer_q2');
    sessionStorage.removeItem('answer_q3');
    sessionStorage.removeItem('answer_q4');
    sessionStorage.removeItem('answer_q5');
    sessionStorage.removeItem('answer_q6');
    sessionStorage.removeItem('answer_q7');
    sessionStorage.removeItem('complaint_checker_completed');
    sessionStorage.removeItem('complaintChecker');

    // Navigate to homepage
    navigate('/');
  };

  if (!content) {
    return (
      <div className="landing-page landing-page--compact">
        <div className="landing-content landing-content--compact">
          <div className="landing-header">
            <p className="landing-label">Complaint checker</p>
            <h1 className="landing-title">We're sorry</h1>
          </div>
          <p>Something went wrong. Please try again.</p>
          <div className="negative-pathway__actions-wrapper">
            <div className="negative-pathway__actions">
              <Button variant="cancel" onClick={handleBack}>
                Back
              </Button>
              <Button variant="continuation" onClick={() => navigate('/')}>
                Return to home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page landing-page--compact">
      <div className="landing-content landing-content--compact">
        <div className="landing-header">
          <p className="landing-label">Complaint checker</p>
          <h1 className="landing-title">We're sorry</h1>
        </div>
        
        <p className="negative-pathway__intro">{content.introText}</p>
        
        <div className="negative-pathway__divider" />
        
        <div className="negative-pathway__section">
          <p className="negative-pathway__question">{content.questionText}</p>
          <div className="negative-pathway__selected-option">
            <span className="negative-pathway__radio-indicator" />
            <span>{content.selectedOption}</span>
          </div>
        </div>
        
        <div className="negative-pathway__divider" />
        
        {content.helpText && (
          <p className="negative-pathway__help-text">{content.helpText}</p>
        )}
        
        {content.links && content.links.length > 0 && (
          <ul className="negative-pathway__links-list">
            {content.links.map((link, index) => (
              <li key={index}>
                <a href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.label}
                </a>
                {link.description && ` - ${link.description}`}
              </li>
            ))}
          </ul>
        )}
        
        {content.additionalContent && content.additionalContent.length > 0 && (
          <div className="negative-pathway__additional">
            {content.additionalContent.map((item, index) => (
              <AdditionalContentRenderer key={index} item={item} />
            ))}
          </div>
        )}
        
        <div className="negative-pathway__divider" />
        
        <div className="negative-pathway__actions-wrapper">
          <div className="negative-pathway__actions">
            <Button variant="cancel" onClick={handleBack}>
              Back
            </Button>
            <Button variant="continuation" onClick={handleReturnHome}>
              Return to home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NegativePathway;
